import { config } from '@/config';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

export interface MeResponse {
  id: string;
  email: string;
  username: string;
  displayName?: string;
  firebaseId?: string;
  businessUnit?: string;
}

const waitForAuthReady = (): Promise<string> => {
  return new Promise((resolve, reject) => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      if (user) {
        resolve(user.uid);
      } else {
        reject(new Error('User not authenticated'));
      }
    });
  });
};

export const fetchUserProfile = async (): Promise<MeResponse> => {
  const auth = getAuth();
  let currentUser = auth.currentUser;

  if (!currentUser) {
    await waitForAuthReady();
    currentUser = auth.currentUser;
  }

  if (!currentUser) {
    throw new Error('User not authenticated');
  }

  const idToken = await currentUser.getIdToken();

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  let response;
  try {
    response = await fetch(`${config.identity.url}/me`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
  } catch (err) {
    clearTimeout(timeoutId);
    if (err instanceof Error && err.name === 'AbortError') {
      throw new Error('Profile fetch timeout');
    }
    throw err;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage =
      errorData.message || `Failed to fetch profile: ${response.status} ${response.statusText}`;
    throw new Error(errorMessage);
  }

  const data = await response.json();
  return data;
};
